import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../lib/api';
import { useSiteSettings, buildWhatsAppUrl } from '../lib/settings';
import Header from '../components/Header';
import { Footer } from '../components/MoreSections';
import OwnerListingCard from '../components/OwnerListingCard';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Home, ChevronRight, ChevronLeft, MapPin, Ruler, Layers, User, MessageCircle, BadgeCheck, Compass, Sparkles, Building2, Home as HomeIcon, Camera } from 'lucide-react';

const OwnerListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { whatsapp_number } = useSiteSettings();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true); setError(null); setActiveImg(0);
    window.scrollTo(0, 0);
    axios.get(`${API}/owner-listings/${id}`)
      .then((r) => setListing(r.data))
      .catch((e) => setError(e.response?.status === 404 ? 'Listing not found' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!listing) return;
    axios.get(`${API}/owner-listings`, { params: { intent: listing.intent, limit: 4 } })
      .then((r) => setRelated((r.data.items || []).filter((x) => x.id !== listing.id).slice(0, 3)))
      .catch(() => {});
  }, [listing]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
          <Skeleton className="h-96 rounded-2xl" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </div>
    );
  }
  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-24 text-center">
          <h1 className="text-2xl font-black text-gray-900 mb-2">{error || 'Something went wrong'}</h1>
          <Button onClick={() => navigate('/owner-listings')} className="bg-[#9a1b2f] hover:bg-[#7a1524]">Browse Owner Listings</Button>
        </div>
      </div>
    );
  }

  const CatIcon = listing.listing_category === 'commercial' ? Building2 : HomeIcon;
  const wa = buildWhatsAppUrl(whatsapp_number, `Hi SafeHouse, I'm interested in the owner-listed property "${listing.property_name}" (${listing.expected_price || ''})`);
  const initial = (listing.name || 'O').trim().charAt(0).toUpperCase();
  const intentLabel = listing.intent === 'rent' ? 'For Rent' : 'For Sale';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-[#9a1b2f] flex items-center gap-1"><Home className="w-3 h-3" /> Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/owner-listings" className="hover:text-[#9a1b2f]">Owner Listings</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium line-clamp-1">{listing.property_name || `${listing.bhk} in ${listing.city}`}</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {Array.isArray(listing.images) && listing.images.length > 0 ? (
                <>
                  {/* Photo gallery */}
                  <div className="relative aspect-[16/10] bg-gray-100">
                    <img src={listing.images[activeImg]} alt={listing.property_name || 'Property photo'} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <Badge className="bg-white text-[#9a1b2f] hover:bg-white shadow-md border-0 font-bold">{intentLabel}</Badge>
                      <Badge className="bg-black/60 text-white hover:bg-black/60 border-0 flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Owner Listed</Badge>
                    </div>
                    {listing.images.length > 1 && (
                      <>
                        <button onClick={() => setActiveImg((i) => (i - 1 + listing.images.length) % listing.images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md">
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={() => setActiveImg((i) => (i + 1) % listing.images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md">
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs flex items-center gap-1">
                          <Camera className="w-3 h-3" /> {activeImg + 1} / {listing.images.length}
                        </div>
                      </>
                    )}
                  </div>
                  {listing.images.length > 1 && (
                    <div className="p-3 flex gap-2 overflow-x-auto">
                      {listing.images.map((im, i) => (
                        <button key={i} onClick={() => setActiveImg(i)} className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition ${i === activeImg ? 'border-[#9a1b2f]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                          <img src={im} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Fallback: gradient hero banner when no photos */
                <div className="relative h-56 md:h-64 bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white p-6 flex items-end">
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white text-[#9a1b2f] hover:bg-white shadow-md border-0 font-bold">{intentLabel}</Badge>
                  </div>
                  <div className="pointer-events-none absolute inset-0 opacity-10">
                    <div className="absolute -right-10 -bottom-10 w-52 h-52 rounded-full border-[10px] border-white" />
                    <div className="absolute right-24 top-4 w-24 h-24 rounded-full border-[4px] border-white" />
                  </div>
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold flex items-center gap-1">
                      <BadgeCheck className="w-3 h-3" /> Owner Listed • SafeHouse Verified
                    </div>
                    <h1 className="text-2xl md:text-3xl font-black mt-1">{listing.property_name || `${listing.bhk} in ${listing.city}`}</h1>
                    <div className="flex items-center gap-1 text-sm opacity-90 mt-1"><MapPin className="w-4 h-4" /> {[listing.location, listing.city].filter(Boolean).join(', ')}</div>
                  </div>
                </div>
              )}

              <div className="p-6">
                {Array.isArray(listing.images) && listing.images.length > 0 && (
                  <div className="mb-4">
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900">{listing.property_name || `${listing.bhk} in ${listing.city}`}</h1>
                    <div className="flex items-center gap-1 text-sm text-gray-600 mt-1"><MapPin className="w-4 h-4" /> {[listing.location, listing.city].filter(Boolean).join(', ')}</div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="capitalize flex items-center gap-1"><CatIcon className="w-3 h-3" />{listing.listing_category}</Badge>
                  <Badge variant="outline" className="capitalize">{listing.property_type}</Badge>
                  {listing.bhk && <Badge className="bg-[#fef2f4] text-[#9a1b2f] hover:bg-[#fef2f4] border-0">{listing.bhk}</Badge>}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                  {[
                    { icon: Ruler, label: 'Area', value: listing.sqft ? `${listing.sqft} sqft` : '—' },
                    { icon: Layers, label: 'Configuration', value: listing.bhk || '—' },
                    { icon: Sparkles, label: listing.intent === 'rent' ? 'Rent' : 'Expected Price', value: listing.expected_price || 'On Request' },
                  ].map((s) => (
                    <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-xs text-gray-500"><s.icon className="w-3.5 h-3.5" /> {s.label}</div>
                      <div className="font-bold text-gray-900 text-sm mt-1">{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {listing.message && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Owner's Note</h2>
                <p className="text-sm text-gray-600 leading-relaxed">“{listing.message}”</p>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Why choose owner-listed properties?</h2>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                {[
                  { title: 'No Brokerage', desc: 'Deal directly with owners — zero broker fees.' },
                  { title: 'Verified by SafeHouse', desc: 'Our team screens every listing for authenticity.' },
                  { title: 'Faster Negotiations', desc: 'Direct owner conversations speed up your deal.' },
                ].map((f) => (
                  <div key={f.title} className="p-4 rounded-xl bg-gray-50">
                    <div className="font-bold text-gray-900">{f.title}</div>
                    <div className="text-xs text-gray-600 mt-1">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white font-black text-2xl flex items-center justify-center shrink-0">
                  {initial}
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-400 font-bold tracking-widest">Owner</div>
                  <div className="font-bold text-gray-900">{listing.name}</div>
                  <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Verified</div>
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <a href={wa} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#25D366] hover:bg-[#20b955] h-11">
                    <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
                  </Button>
                </a>
              </div>
              <div className="text-[11px] text-gray-500 mt-3 text-center">Contact details protected — all inquiries route through SafeHouse.</div>
            </div>

            <div className="bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white rounded-2xl p-5">
              <h4 className="font-bold">Selling your property?</h4>
              <p className="text-xs opacity-90 mt-1">List for free and reach thousands of buyers.</p>
              <Link to="/"><Button className="mt-3 w-full bg-white text-[#9a1b2f] hover:bg-gray-50" size="sm">Post Property Free</Button></Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-5 flex items-center gap-2"><User className="w-5 h-5 text-[#9a1b2f]" /> More Owner Listings</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((l) => <OwnerListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OwnerListingDetailPage;
